import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatError, MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { Router } from '@angular/router';
import { AskForRadioService, ControlErrorDirective, DialogResultTypes, HowdenTableComponent, IAskForRadioData, IButtonActionData, IHowdenItem, IPageOf, ITableEventData, PageTitleComponent, UIBlockerService } from '@howdeniberia/core-front';
import { tap } from 'rxjs';
import { ReportType } from 'src/app/core/enums';
import { IMap } from 'src/app/core/models/api';
import { IMapReportInfo } from 'src/app/core/models/front';
import { IMapService } from 'src/app/core/services/api/map';
import { FileUtils, Limits } from 'src/app/shared/utils';
import { SubSink } from 'subsink';
import { MapMapper } from '../../mapper';
import { MapSearchViewModel } from '../../models';

@Component({
  selector: 'howden-map-search',
  templateUrl: './map-search.component.html',
  styleUrl: './map-search.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatInput,
    MatLabel,
    MatFormField,
    MatError,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    ControlErrorDirective,
    PageTitleComponent,
    HowdenTableComponent
  ]
})
export class MapSearchComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private mapSrv = inject(IMapService);
  private uiBlockerSrv = inject(UIBlockerService);
  private askForRadioSrv = inject(AskForRadioService);

  model = new MapSearchViewModel();

  get minDate(): Date { return Limits.minDate; }

  private _subscriptions = new SubSink();

  ngOnInit(): void {
    this.refresh();
    this.onChanges();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  onServerSideConfigChanged(event: ITableEventData): void {
    this.model.updateServerSideConfig(event);
    this.refresh();
  }

  onAction(event: IButtonActionData): void {
    if (event.buttonName === 'edit') {
      this.onEdit(event);
    } else if (event.buttonName === 'download') {
      this.onDownload(event);
    }
  }

  private onChanges(): void {
    this._subscriptions.sink = this.model.filterForm.valueChanges.subscribe(() => {
      this.model.searchRequest.pageNumber = 0;
      this.model.updateServerSideFilters();
      this.refresh();
    });
  }

  private refresh(): void {
    this.model.updateServerSideFilters();

    this.uiBlockerSrv.block();

    this.mapSrv.search(this.model.searchRequest).subscribe({
      next: (searchResult: IPageOf<IMap>) => {
        this.model.length = searchResult.totalCount;
        this.model.data = searchResult.data?.map((source) =>
          MapMapper.mapForSearch(source)
        );
      },
      complete: () => {
        this.uiBlockerSrv.unblock();
      }
    });
  }

  private onEdit(event: IButtonActionData) {
    this.router.navigate([`management/risks-evaluation/${event.row.mapId}`]);
  }

  private onDownload(event: IButtonActionData) {
    this.uiBlockerSrv.block();

    const mapId = event.row.mapId;

    const options = {
      title: $localize`:@@app.map.search.download.title:Descarga del Mapa de Riesgos`,
      subTitle: $localize`:@@app.map.search.download.docType:Seleccione el tipo de documento`,
      initialValue: ReportType.Pdf,
      okActionText: $localize`:@@app.map.search.download.action:Descarga`,
      allowCancel: true,
      items: [
        { key: ReportType.Pdf, description: 'Pdf (.pdf)' } as IHowdenItem,
        { key: ReportType.Word, description: 'Word (.docx)' } as IHowdenItem
      ],
      widthPixels: 350
    } as IAskForRadioData;

    this.askForRadioSrv.open(options).pipe(
      tap((result: any) => {
        if (result.result === DialogResultTypes.OK) {
          const reportInfo = {
            mapId: mapId,
            type: result.resultData.key
          } as IMapReportInfo;

          this.mapSrv.downloadReport(reportInfo).subscribe(
            {
              next: (blob: Blob) => {
                const attachName = FileUtils.formatFilename(
                  'RiskMap',
                  reportInfo.type === ReportType.Word ? 'docx' : 'pdf'
                );
                FileUtils.downloadFile(
                  attachName,
                  blob
                );
              },
              complete: () => {
                this.uiBlockerSrv.unblock();
              }
            }
          );
        }
      })
    ).subscribe();
  }
}
